import useLocation from "@DSG/Common/Hooks/useLocation";
import CoveoCartLoadChainTap from "@DSG/Components/Coveo/Commerce/CoveoReduxCartChainTap";
import CoveoPurchaseChainTap from "@DSG/Components/Coveo/Commerce/CoveoReduxPurchaseChainTap";
import initializeCoveoCommerce from "@DSG/Handlers/Coveo/CoveoInitHandler";
import { getSettingsCollection } from "@DSG/Overrides/Store/Context/ContextSelectors";
import CoveoApplicationState from "@DSG/Store/Data/CoveoApplicationState/CoveoApplicationState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { addToChainAfter } from "@insite/client-framework/HandlerCreator";
import { SettingsCollectionModel } from "@insite/client-framework/Services/SettingsService";
import {
  chain as LoadCartChain,
  ExecuteOnSuccessCallback
} from '@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart';
import {
  chain as PurchaseCartChain,
  DispatchCompletePlaceOrder
} from '@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder';
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import Page from "@insite/mobius/Page";
import * as React from "react";
import { ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled from "styled-components";

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showCompactHeader: boolean;
    compactHeader: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    showSignInOverlay?: boolean;
    signInOverlay?: ReactNode;
    showStorePickupOverlay?: boolean;
    storePickupOverlay?: ReactNode;
    showAddressBookOverlay?: boolean;
    addressBookOverlay?: ReactNode;
}

const mapStateToProps = (state: CoveoApplicationState) => {
    return {
         coveoCommerce: state.DSGGlobalData,
         customSettings: getSettingsCollection(state).customSettings,
         customerBranch: state.pages.accountSettings.editingAccount?.defaultWarehouse?.properties.abbreviation 
                      || state.context.session.pickUpWarehouse?.properties.abbreviation,
    };
};

const mapDispatchToProps = {
    initializeCoveoCommerce
};

type Props = PageLayoutProps 
    & ReturnType<typeof mapStateToProps>
    & ResolveThunks<typeof mapDispatchToProps>;

// This doesn't currently support HMR
const PageLayout = ({
    showHeader,
    header,
    showBreadcrumbs,
    pageContent,
    showFooter,
    footer,
    showCompactHeader,
    compactHeader,
    showSignInOverlay,
    signInOverlay,
    showStorePickupOverlay,
    storePickupOverlay,
    showAddressBookOverlay,
    addressBookOverlay,
    initializeCoveoCommerce,
    customSettings,
    customerBranch,
    coveoCommerce: {
        CartController,
        RedirectTriggerState
    }
}: Props) => {
    const settings = customSettings as SettingsCollectionModel;
    const isCoveoEnabled = settings.enableCoveoSearch;

    const Location = useLocation();

    const [reduxHandlersPlaced, setReduxHandlersPlaced] = useState(false);
    
    // Initialize Coveo Commerce
    useEffect(() => {
        if (!isCoveoEnabled) { return; }
        initializeCoveoCommerce();
    }, []);

    useEffect(() => {
      const customerBranchCookie = document.cookie.split('; ').find(row => row.startsWith('customerBranch='));

      // Set the customerBranch cookie if it doesn't exist or if the value is different
      if (customerBranch && customerBranchCookie) {
          const cookieValue = customerBranchCookie.split('=')[1];
          if (cookieValue !== customerBranch) {
              document.cookie = `customerBranch=${customerBranch}; max-age=${24 * 60 * 60 * .95};path=/`;
              window.location.reload();
          }
      }
    }, [customerBranch]);

    // Place Redux Handlers for Coveo Commerce
    useEffect(() => {
      try {
          // Check if CartController are defined
          if (!CartController) {
              console.error("PageLayout >> CartController is not defined.");
              return;
          }
          
          if (!reduxHandlersPlaced) {
              addToChainAfter(LoadCartChain, ExecuteOnSuccessCallback, CoveoCartLoadChainTap(CartController));
              addToChainAfter(PurchaseCartChain, DispatchCompletePlaceOrder, CoveoPurchaseChainTap(CartController));
              setReduxHandlersPlaced(true);
          }
      } catch (error) {
          console.error("PageLayout >> Error placing Redux handlers: ", error);
      }
    }, [CartController, reduxHandlersPlaced]);

    // Handles Redirect Triggers
    useEffect(() => {
      const isSearchPage = window.location.pathname.includes('/search');
      if(!isSearchPage || !isCoveoEnabled || !RedirectTriggerState || !RedirectTriggerState.redirectTo) { return; }
      
      window.location.replace(RedirectTriggerState.redirectTo);
    }, [RedirectTriggerState, Location.pathname]);

    return (
        <PageLayoutWrapper>
            {showHeader && header}
            {showCompactHeader && compactHeader}
            {showBreadcrumbs && (
                <Page as="div">
                    <PageBreadcrumbs />
                </Page>
            )}
            {pageContent}
            {showFooter && footer}
            {showSignInOverlay && signInOverlay}
            {showStorePickupOverlay && storePickupOverlay}
            {showAddressBookOverlay && addressBookOverlay}
            <StyledWrapper className="backgroundSearchOverlay hidden">
            </StyledWrapper>
        </PageLayoutWrapper>
    );
};

const PageLayoutWrapper = styled.div`
    display: flex;
    min-height: 100vh;
    flex-direction: column;
    > main {
        flex: 1 1 0%;
    }

    @media print {
        display: block;
    }
`;

export default connect(mapStateToProps, mapDispatchToProps) (PageLayout);